import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Clock, Send, Download, ShoppingBag, Gift, ArrowUp, ArrowDown, ChevronLeft, ChevronRight, RefreshCw, Coins, Flame, Copy, Check, Wallet } from 'lucide-react';
import axios from 'axios';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import useEscapeKey from '../hooks/useEscapeKey';

const WalletHistoryModal = ({ isOpen, onClose, onBack, onOpenSend, onOpenBuy, onOpenReceive, onOpenWithdraw, initialTab = 'fires' }) => {
  useEscapeKey(onClose, isOpen);
  const { user, isAdmin, isTote } = useAuth();
  const [page, setPage] = useState(0);
  const [activeTab, setActiveTab] = useState(initialTab); // 'coins' | 'fires'
  const [copiedAddress, setCopiedAddress] = useState(false);

  const handleCopyAddress = async () => {
    if (!user?.wallet_address) return;
    try {
      await navigator.clipboard.writeText(user.wallet_address);
      setCopiedAddress(true);
      setTimeout(() => setCopiedAddress(false), 2000);
    } catch (err) {
      console.error('Error copying address:', err);
    }
  };
  const pageSize = 25;

  // Usar React Query para manejar transacciones con refetch automático
  const { data, isLoading: loading } = useQuery({
    queryKey: ['wallet-transactions', user?.id, activeTab, page],
    queryFn: async () => {
      if (!user?.id || user.id === 'undefined' || user.id === 'null') return { transactions: [], total: 0 };
      const response = await axios.get(`/api/profile/${user.id}/transactions`, {
        params: {
          currency: activeTab,
          limit: pageSize,
          offset: page * pageSize
        }
      });
      return response.data;
    },
    enabled: isOpen && !!user?.id && user.id !== 'undefined' && user.id !== 'null',
    refetchInterval: 5000,
    refetchIntervalInBackground: false
  });

  const { data: redeemsHistory } = useQuery({
    queryKey: ['my-redeems'],
    enabled: isOpen && activeTab === 'fires' && !!user,
    queryFn: async () => {
      const response = await axios.get('/api/market/my-redeems');
      return response.data;
    }
  });

  // Sincronizar pestaña inicial cuando cambia initialTab o se abre el modal
  useEffect(() => {
    if (!isOpen) return;
    const normalized = initialTab === 'coins' ? 'coins' : 'fires';
    setActiveTab(normalized);
    setPage(0);
  }, [initialTab, isOpen]);

  const transactions = data?.transactions || [];
  const total = data?.total || 0;

  const canSeeOpeningBalance = (isAdmin?.() || isTote?.());

  // Reset page cuando cambia el tab
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setPage(0);
  };

  const getTransactionIcon = (type) => {
    switch (type) {
      case 'transfer_in':
        return <ArrowDown size={16} className="text-green-400" />;
      case 'transfer_out':
        return <ArrowUp size={16} className="text-red-400" />;
      case 'fire_purchase':
        return <ShoppingBag size={16} />;
      case 'bingo_card_purchase':
        return <ShoppingBag size={16} className="text-purple-400" />;
      case 'bingo_card_refund':
        return <RefreshCw size={16} className="text-blue-400" />;
      case 'welcome_bonus':
      case 'game_reward':
      case 'tictactoe_win':
      case 'tictactoe_draw':
      case 'game_win':
        return <Gift size={16} />;
      case 'commission':
        return <ArrowDown size={16} className="text-green-400" />;
      case 'game_bet':
      case 'tictactoe_bet':
      case 'game_spend':
        return <Clock size={16} className="text-orange-400" />;
      case 'tictactoe_refund':
        return <RefreshCw size={16} className="text-blue-400" />;
      default:
        return <Clock size={16} />;
    }
  };

  const getTransactionLabel = (type) => {
    if (!type) return 'Desconocido';
    const labels = {
      transfer_in: 'Recibido',
      transfer_out: 'Enviado',
      fire_purchase: 'Compra de Fuegos',
      bingo_card_purchase: 'Compra Cartón Bingo',
      bingo_card_refund: 'Reembolso Bingo',
      welcome_bonus: 'Bono de Bienvenida',
      game_reward: 'Premio de Juego',
      game_win: 'Victoria en Juego',
      game_spend: 'Gasto en Juego',
      commission: 'Comisión',
      opening_balance: 'Balance inicial',
      admin_grant: 'Regalo Admin',
      game_bet: 'Apuesta de Juego',
      tictactoe_bet: 'Apuesta TicTacToe',
      tictactoe_win: 'Victoria TicTacToe',
      tictactoe_draw: 'Empate TicTacToe',
      tictactoe_refund: 'Devolución TicTacToe',
      experience_purchase: 'Compra de Experiencia'
    };
    return labels[type] || type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Determinar si una transacción es un débito (debe mostrar signo negativo)
  const isDebitTransaction = (type, amount) => {
    const debitTypes = [
      'debit',
      'transfer_out',
      'game_bet',
      'tictactoe_bet',
      'raffle_cost',
      'raffle_number_purchase',
      'raffle_creation_fee',
      'raffle_prize_fire_payment_out',
      'market_redeem',
      'fire_burn',
      'bingo_card_purchase',
      'experience_purchase'
    ];
    
    // Verificar si el tipo contiene palabras clave de débito
    const typeStr = (type || '').toLowerCase();

    if (typeStr === 'fire_purchase') return false;
    if (debitTypes.includes(typeStr)) return true;
    if (typeStr.includes('cost') || typeStr.includes('bet') || 
        typeStr.includes('burn') || typeStr.includes('spend') ||
        typeStr.includes('purchase')) return true;
    
    // Fallback: usar signo del monto si el tipo no es conocido
    const amt = parseFloat(amount);
    if (!isNaN(amt) && amt < 0) return true;
    return false;
  };

  // Formatear el monto con el signo correcto
  const formatAmount = (amount, type) => {
    const parsed = parseFloat(amount);
    const value = Number.isFinite(parsed) ? Math.abs(parsed) : 0;
    const isDebit = isDebitTransaction(type, amount);
    const sign = isDebit ? '-' : '+';
    return `${sign}${value.toFixed(2)}`;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', { 
      day: '2-digit', 
      month: 'short', 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const totalPages = Math.ceil(total / pageSize);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const getCurrencyIcon = () => {
    return activeTab === 'fires' ? '🔥' : '🪙';
  };

  const visibleTransactions = canSeeOpeningBalance
    ? transactions
    : transactions.filter((tx) => String(tx.type).toLowerCase() !== 'opening_balance');

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto p-3 sm:p-4 bg-black/60 backdrop-blur-sm"
          onClick={handleBackdropClick}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="w-full max-w-2xl card-glass my-auto max-h-[92vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header - Fixed at top */}
            <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-theme shrink-0">
              <div className="flex items-center gap-3">
                {onBack && (
                  <button
                    type="button"
                    onClick={onBack}
                    className="p-2 hover:bg-surface-2 rounded-lg transition-colors text-text-secondary hover:text-text"
                    title="Volver"
                  >
                    <ChevronLeft size={20} />
                  </button>
                )}
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <Clock size={20} className="text-primary" />
                </div>
                <h2 className="text-xl font-bold text-text">Historial de Wallet</h2>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-surface-2 rounded-lg transition-colors text-text-secondary hover:text-text"
              >
                <X size={20} />
              </button>
            </div>

            {/* Scrollable Content Area - Encompasses Tabs, Address, Actions, and Transactions */}
            <div className="flex-1 min-h-0 overflow-y-auto p-4 sm:p-6 pr-2.5 sm:pr-3.5 space-y-4 custom-scrollbar">
              {/* Tabs */}
              <div className="flex gap-2 p-1.5 bg-surface-1 rounded-xl border border-theme">
                <button
                  onClick={() => handleTabChange('coins')}
                  className={`flex-1 py-2.5 px-4 rounded-lg transition-all duration-200 flex items-center justify-center gap-2 font-bold text-sm ${
                    activeTab === 'coins'
                      ? 'bg-[#FEF7E0] text-[#B06000] border border-[#FEEFC3] shadow-sm'
                      : 'text-text-secondary hover:text-text hover:bg-surface-2'
                  }`}
                >
                  <Coins size={18} />
                  <span>Monedas</span>
                </button>
                <button
                  onClick={() => handleTabChange('fires')}
                  className={`flex-1 py-2.5 px-4 rounded-lg transition-all duration-200 flex items-center justify-center gap-2 font-bold text-sm ${
                    activeTab === 'fires'
                      ? 'bg-[#FCE8E6] text-[#C5221F] border border-[#FAD2CF] shadow-sm'
                      : 'text-text-secondary hover:text-text hover:bg-surface-2'
                  }`}
                >
                  <Flame size={18} />
                  <span>Fuegos</span>
                </button>
              </div>

              {/* Wallet Address Display */}
              {user?.wallet_address && (
                <div className="p-3 flex items-center justify-between gap-3 bg-surface-1 border border-theme rounded-xl">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                      <Wallet size={16} className="text-primary" />
                    </div>
                    <div className="min-w-0">
                      <span className="block text-[10px] text-text-secondary font-bold uppercase tracking-wider">Dirección de Billetera</span>
                      <span className="block font-mono text-[11px] text-primary font-semibold truncate max-w-[200px] sm:max-w-none">
                        {user.wallet_address}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={handleCopyAddress}
                    className="py-1.5 px-2.5 bg-surface-2 hover:bg-surface-3 active:scale-95 rounded-lg border border-theme text-text transition-all shrink-0 flex items-center gap-1.5 text-xs font-semibold"
                    title="Copiar Dirección"
                  >
                    {copiedAddress ? (
                      <>
                        <Check size={14} className="text-emerald-600" />
                        <span className="text-[10px] text-emerald-600 font-bold">Copiado</span>
                      </>
                    ) : (
                      <>
                        <Copy size={14} />
                        <span className="text-[10px]">Copiar</span>
                      </>
                    )}
                  </button>
                </div>
              )}

              {/* Action Buttons - Solo para Fuegos */}
              {activeTab === 'fires' && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <button
                    onClick={() => {
                      onClose();
                      onOpenSend?.();
                    }}
                    className="py-3 px-2 bg-blue-50/80 hover:bg-blue-100/80 text-blue-800 rounded-xl transition-all border border-blue-200/80 flex flex-col items-center justify-center gap-1 group shadow-sm"
                  >
                    <Send size={18} className="text-primary group-hover:scale-110 transition-transform" />
                    <span className="text-[11px] font-bold uppercase tracking-wider">Enviar</span>
                  </button>
                  <button
                    onClick={() => {
                      onClose();
                      onOpenBuy?.();
                    }}
                    className="py-3 px-2 bg-emerald-50/80 hover:bg-emerald-100/80 text-emerald-800 rounded-xl transition-all border border-emerald-200/80 flex flex-col items-center justify-center gap-1 group shadow-sm"
                  >
                    <ShoppingBag size={18} className="text-emerald-700 group-hover:scale-110 transition-transform" />
                    <span className="text-[11px] font-bold uppercase tracking-wider">Comprar</span>
                  </button>
                  <button
                    onClick={() => {
                      onClose();
                      onOpenWithdraw?.();
                    }}
                    className="py-3 px-2 bg-amber-50/80 hover:bg-amber-100/80 text-amber-900 rounded-xl transition-all border border-amber-200/80 flex flex-col items-center justify-center gap-1 group shadow-sm"
                  >
                    <RefreshCw size={18} className="text-amber-700 group-hover:scale-110 transition-transform" />
                    <span className="text-[11px] font-bold uppercase tracking-wider">Retirar</span>
                  </button>
                  <button
                    onClick={() => {
                      onClose();
                      onOpenReceive?.();
                    }}
                    className="py-3 px-2 bg-purple-50/80 hover:bg-purple-100/80 text-purple-900 rounded-xl transition-all border border-purple-200/80 flex flex-col items-center justify-center gap-1 group shadow-sm"
                  >
                    <Download size={18} className="text-purple-700 group-hover:scale-110 transition-transform" />
                    <span className="text-[11px] font-bold uppercase tracking-wider">Recibir</span>
                  </button>
                </div>
              )}

              {/* Transactions List */}
              <div className="space-y-2">
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                  </div>
                ) : visibleTransactions.length === 0 ? (
                  <div className="text-center py-12">
                    <Clock size={48} className="mx-auto text-text-muted mb-3 opacity-40" />
                    <p className="text-text-secondary text-sm">No hay transacciones de {activeTab === 'fires' ? 'fuegos' : 'monedas'}</p>
                  </div>
                ) : (
                  visibleTransactions.map((tx) => (
                    <motion.div
                      key={tx.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="p-3.5 rounded-xl bg-surface-1 border border-theme hover:border-slate-300 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        {/* Icon */}
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center border ${
                          isDebitTransaction(tx.type, tx.amount) 
                            ? 'bg-red-50 border-red-200 text-red-600' 
                            : 'bg-emerald-50 border-emerald-200 text-emerald-700'
                        }`}>
                          {getTransactionIcon(tx.type)}
                        </div>

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-semibold text-sm sm:text-base text-text">{getTransactionLabel(tx.type)}</span>
                            <span className={`font-black text-sm sm:text-base ${isDebitTransaction(tx.type, tx.amount) ? 'text-red-600' : 'text-emerald-700'}`}>
                              {formatAmount(tx.amount, tx.type)} {getCurrencyIcon()}
                            </span>
                          </div>
                          {tx.type === 'market_redeem' && (
                            <div className="mb-2">
                               {(() => {
                                 const redeem = (redeemsHistory || []).find(r => 
                                   r.fires_amount === Math.abs(parseFloat(tx.amount)) && 
                                   Math.abs(new Date(r.created_at) - new Date(tx.created_at)) < 60000
                                 );
                                 if (!redeem) return null;
                                 
                                 const statusColors = {
                                   pending: 'bg-orange-500/20 text-orange-400 border-orange-500/20',
                                   approved: 'bg-green-500/20 text-green-400 border-green-500/20',
                                   rejected: 'bg-red-500/20 text-red-400 border-red-500/20'
                                 };
                                 
                                 const statusLabels = {
                                   pending: 'PENDIENTE',
                                   approved: 'APROBADO',
                                   rejected: 'RECHAZADO'
                                 };

                                 return (
                                   <div className="space-y-2 mt-1">
                                     <span className={`text-[9px] font-black px-2 py-0.5 rounded-full border ${statusColors[redeem.status] || ''}`}>
                                       {statusLabels[redeem.status] || redeem.status.toUpperCase()}
                                     </span>
                                     {redeem.processor_notes && (
                                       <p className="text-[10px] text-text/40 italic bg-black/20 p-2 rounded-lg mt-2">
                                         "{redeem.processor_notes}"
                                       </p>
                                     )}
                                   </div>
                                 );
                               })()}
                            </div>
                          )}
                          {tx.description && tx.type !== 'market_redeem' && (
                            <p className="text-xs text-text/60 truncate">{tx.description}</p>
                          )}
                          {tx.fiat_amount_ves != null && (
                            <p className="text-xs text-text/50 mt-0.5">
                              {(() => {
                                const ves = parseFloat(tx.fiat_amount_ves);
                                const usdt = tx.usdt_equivalent != null ? parseFloat(tx.usdt_equivalent) : null;
                                const safeVes = Number.isFinite(ves) ? ves : 0;
                                const vesText = safeVes.toFixed(2);
                                if (usdt != null && Number.isFinite(usdt)) {
                                  return `Snapshot FIAT: ${vesText} Bs (≈ ${usdt.toFixed(2)} USD)`;
                                }
                                return `Snapshot FIAT: ${vesText} Bs`;
                              })()}
                            </p>
                          )}
                          <div className="flex items-center justify-between mt-1">
                            <span className="text-xs text-text/40">
                              {formatDate(tx.created_at)}
                            </span>
                            <span className="text-xs text-text/40">
                              {(() => {
                                const parsed = Number(tx.balance_after);
                                const balanceValue = Number.isFinite(parsed) ? parsed : 0;
                                return `Balance: ${balanceValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${getCurrencyIcon()}`;
                              })()}
                            </span>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))
                )}
              </div>
            </div>

            {/* Pagination - Fixed at bottom */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-t border-theme bg-surface-1 shrink-0">
                <button
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="p-2 hover:bg-surface-2 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed text-primary"
                  aria-label="Página anterior"
                >
                  <ChevronLeft size={20} />
                </button>

                <span className="text-xs font-semibold text-text-secondary uppercase tracking-wider">
                  Pág {page + 1} / {totalPages} <span className="opacity-60 ml-2">({total} tx)</span>
                </span>

                <button
                  onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1}
                  className="p-2 hover:bg-surface-2 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed text-primary"
                  aria-label="Página siguiente"
                >
                  <ChevronRight size={20} />
                </button>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default WalletHistoryModal;
